export const ApckPropertyTable = {
    /* fields format
    {
        "key": "login_url",
        "value": "",
        "type": "text",
        "is_property": true, // The key cannot be changed
    },
    */
    props: {
        value: {
            type: [Object, String],
            required: true,
        },
        fields: {
            type: Array,
            default: function () {
                return [];
            },
        },
        canAdd: {
            type: Boolean,
            default: true,
        },
        // TODO: add logic
        canDelete: {
            type: Boolean,
            default: true,
        },
        canEdit: {
            type: Boolean,
            default: true,
        },
        keyRegex: {
            type: RegExp,
            default: () => /[^\w-]/g,
        },
        confirmationMessage: {
            type: String,
            default: 'Are you sure you want to delete the item',
        },
        confirmationEmptyMessage: {
            type: String,
            default: 'Are you sure you want to delete the unnamed item',
        },
        confirmationResetMessage: {
            type: String,
            default: 'Are you sure you want to reset the item',
        },
        confirmationItem: {
            type: String,
            default: 'value',
        },
        rows: {
            type: String,
            default: '10',
        },
        isBulkMode: {
            type: Boolean,
            default: true,
        },
        topHeight: {
            type: Number,
            default: 0,
        },
        actionHandler: {
            type: Function,
        },
        actionName: {
            type: String,
            default: '',
        },
        actionIcon: {
            type: String,
            default: '',
        },
        clickEditHandler: {
            type: Function,
        },
        enableCredentialService: {
            type: Boolean,
            default: false,
        },
    },
    data: function () {
        return {
            properties: [],
            isTableView: true,
            textValue: '',
            hiddenCredentials: {},
        };
    },
    watch: {
        value: function (val) {
            if (typeof this.value === 'object') {
                this.loadValues(this.value);
            } else {
                this.loadTextValues(this.value);
            }
        },
    },
    methods: {
        loadValues: function (item) {
            if (!item) return;
            this.properties = [];
            this.hiddenCredentials = {};
            if (this.fields) {
                this.properties = this.fields.map((field) => {
                    return {
                        ...field,
                        editKey: false,
                        editValue: false,
                    };
                });
            }
            Object.keys(item).forEach((key) => {
                const index = this.properties.findIndex((f) => f.key === key);
                let stored_value = item[key];
                let type = undefined;
                let value;
                if (typeof stored_value === 'object') {
                    if (stored_value.type === 'credential') {
                        if (this.enableCredentialService) {
                            value = stored_value.value;
                            type = stored_value.type;
                        } else {
                            // Credman is disabled but a credential object is present (e.g. added by the migrator
                            // script). We need to stash the original credential so unrelated variable edits don't wipe
                            // it in `setValue()` and so the UI doesn't leak it as "stored in CredMan" with the picker.
                            this.hiddenCredentials[key] = stored_value;
                            if (index === -1) return;
                            value = "";
                        }
                    } else {
                        value = JSON.stringify(stored_value);
                    }
                } else {
                    value = stored_value;
                }
                if (index !== -1) {
                    this.properties[index]['value'] = value;
                    if (type) this.properties[index]['type'] = type;
                } else {
                    this.addProperty(key, value, type);
                }
            });
        },
        loadTextValues: function (val) {
            if (!val) return;
            this.textValue = val;
            if (this.isTableView) this.textToFields(val);
        },
        addOrUpdateProperty: function ({key, values}) {
            if (!key) return;
            var index = this.properties.findIndex((f) => f.key === key);
            if (index !== -1) {
                this.$set(this.properties, index, {...this.properties[index], ...values});
                this.setValue();
            } else {
                const value = values['value'];
                const type = values['type'];
                if (value) {
                    this.addProperty(key, value, type || undefined);
                }
            }
        },
        updateTextValue: function (event) {
            if (typeof this.value === 'object') {
                this.textToFields(event.target.value);
                this.setValue();
                this.textValue = event.target.value;
            } else {
                this.$emit('input', event.target.value);
            }
        },
        addNewProperty: function () {
            this.addProperty('', '');
        },
        addProperty: function (key, value, type='text') {
            var new_property = {
                key: key,
                value: value,
                editKey: false,
                editValue: false,
                type: type,
                is_property: false,
            };
            this.properties.push(new_property);
        },
        deleteProperty: function (index) {
            const cmp = this;
            let message = '';
            if (cmp.properties[index].key) {
                message = cmp.confirmationMessage + ` '${cmp.properties[index].key}'?`;
            } else {
                message = cmp.confirmationEmptyMessage;
            }

            cmp.$dialog.confirm(message, `Delete ${cmp.confirmationItem}`, function () {
                cmp.properties.splice(index, 1);
                cmp.setValue();
            });
        },
        resetProperty: function (index) {
            const cmp = this;
            const message = `${cmp.confirmationResetMessage}?`;

            let affected_indexes = [index];

            if (cmp.properties[index].type === 'credential') {
                // if we are resetting a username or password credential, we need to reset both values
                if (['username', 'password'].includes(cmp.properties[index].key)) {
                    const usernameIndex = cmp.properties.findIndex(p => p.key === 'username');
                    const passwordIndex = cmp.properties.findIndex(p => p.key === 'password');
                    if (usernameIndex !== -1) affected_indexes.push(usernameIndex);
                    if (passwordIndex !== -1) affected_indexes.push(passwordIndex);
                }
            }

            cmp.$dialog.confirm(message, `Reset ${cmp.confirmationItem}`, function () {
                for (const i of affected_indexes) {
                    cmp.$set(cmp.properties, i, {...cmp.fields[i]});
                }
                cmp.setValue();
            });
        },
        clickEditValue: function (item) {
            if (this.clickEditHandler) {
                const consumed = this.clickEditHandler(item);
                if (consumed) return;
            }
            if (!this.canEdit) return;
            const cmp = this;
            item.editValue = true;
            this.$nextTick(() => {
                if (cmp.$refs.propertyValue) {
                    cmp.$refs.propertyValue[0].focus();
                }
            });
        },
        stopEditValue: function (item) {
            item.editValue = false;
            this.setValue();
        },
        clickEditKey: function (item) {
            if (item.is_property) return;
            const cmp = this;
            item.editKey = true;
            this.$nextTick(() => {
                if (cmp.$refs.propertyKey) {
                    cmp.$refs.propertyKey[0].focus();
                }
            });
        },
        stopEditKey: function (item) {
            item.editKey = false;
            this.setValue();
        },
        setValue: function () {
            if (typeof this.value === 'object') {
                for (const key in this.value) {
                    if (this.hiddenCredentials[key]) continue;
                    delete this.value[key];
                }
                this.properties.forEach((item) => {
                    if (!item.key) return;
                    if (this.hiddenCredentials[item.key]) {
                        // The row value is a credential object in CredMan but is hidden because they don't have
                        // CredMan enabled, so we need to treat any non-empty value as an override and store that,
                        // otherwise as it's unchanged we keep the hidden credential referencing CredMan.
                        if (item.value == null || item.value === "") return;
                        delete this.hiddenCredentials[item.key];
                    }
                    if (this.enableCredentialService && item.type === 'credential') {
                        this.value[item.key] = {type: item.type, value: item.value};
                    } else {
                        this.value[item.key] = item.value;
                    }
                });
                this.$emit('input', {...this.value});
                this.$emit('change', {...this.value});
            } else {
                const newValue = this.fieldsToText();
                this.$emit('input', newValue);
                this.$emit('change', newValue);
            }
        },
        checkKeyInput: function (item) {
            // check keyRegex
            var value = item.key;
            value = value.toString().replace(this.keyRegex, '');
            item.key = value;
        },
        switchView: function () {
            // TODO: update trigger
            this.isTableView = !this.isTableView;
            if (this.isTableView) {
                this.textToFields(this.textValue);
            } else {
                this.textValue = this.fieldsToText();
            }
        },
        fieldsToText: function () {
            let str = '';
            this.properties.forEach((item) => {
                str += `${item.key}: ${item.value}\n`;
            });
            return str;
        },
        textToFields: function (textValue) {
            // TODO: add default values
            this.properties = [];
            let lines = textValue.split('\n');
            lines.forEach((line) => {
                if (!line.trim()) return;
                let parts = line.split(':');
                let key = parts.shift().trim();
                let value = parts.join(':').trim();
                var index = this.properties.findIndex((f) => f.key == key);
                if (index != -1) this.properties[index]['value'] = value;
                else this.addProperty(key, value);
            });
        },
        clickAction(item) {
            if (this.actionHandler) this.actionHandler(item);
        },
    },
    mounted: function () {
        if (typeof this.value === 'object') {
            this.loadValues(this.value);
        } else {
            this.loadTextValues(this.value);
        }
    },
    computed: {
        textareaStyles() {
            const style = { width: '100%' };
            if (this.topHeight) style.height = this.topHeight - 45 + 'px';
            return style;
        },
        showAction() {
            if (this.actionHandler) return true;
            return false;
        },
        rowClass() {
            if (this.actionHandler) return 'property-table__row--action-left';
            return '';
        },
    },
    template: `
        <div>
        <div v-if="!isTableView && isBulkMode" class="property-table">
            <div :class="['property-table__row', rowClass]">
                <div v-if="showAction" class="property-table__cell"></div>
                <div class="property-table__cell"></div>
                <div class="property-table__cell"></div>
                <div class="property-table__cell">
                    <a
                        class="btn"
                        href="javascript:"
                        style="color:#484848"
                        v-on:click="switchView"
                        id="bulk-switch"
                    >
                        <span class="fa fa-table"></span>
                    </a>
                </div>
            </div>
            <div class="property-table__row property-table__row--textarea">
                <div class="property-table__cell">
                    <textarea
                        :value="textValue"
                        v-on:input="updateTextValue"
                        :rows="rows"
                        :style="textareaStyles"
                    >
                    </textarea>
                </div>
            </div>
        </div>
        <div v-if="isTableView" class="property-table">
            <div :class="['property-table__row', rowClass]">
                <div v-if="showAction" class="property-table__cell"></div>
                <div class="property-table__cell">KEY</div>
                <div class="property-table__cell">VALUE</div>
                <div class="property-table__cell" v-if="canDelete">
                    <a
                        v-if="isBulkMode"
                        class="btn"
                        href="javascript:"
                        style="color:#484848"
                        v-on:click="switchView"
                        id="bulk-switch"
                    >
                        <span class="fa fa-list"></span>
                    </a>
                </div>
            </div>
            <div
                v-for="item, index in properties"
                :key="index"
                :class="['property-table__row', (item.editKey || item.editValue) ? 'property-table__row--highlighted' : '', rowClass]"
            >
                <div v-if="showAction" class="property-table__cell">
                    <a
                        class="btn"
                        href="javascript:"
                        v-on:click="clickAction(item)"
                    >
                        <span class="fa fa-link" style="color:#000;"></span>
                    </a>
                </div>

                <div
                    :class="['property-table__cell', item.is_property ? 'property-table__cell--disabled' : '']"
                    v-on:click="clickEditKey(item)"
                >
                    <span
                        v-if="!item.editKey"
                        class="property-table__cell__text"
                    >
                        <i v-if="enableCredentialService && item.type === 'credential'" class="fa-regular fa-key"></i>
                        {{item.key}}
                    </span>
                    <input
                        v-if="item.editKey"
                        v-model="item.key"
                        v-on:input="checkKeyInput(item)"
                        v-on:blur="stopEditKey(item)"
                        v-on:keyup.enter="stopEditKey(item)"
                        type="text"
                        ref="propertyKey"
                        autocomplete="off"
                    />
                </div>
                <div
                    class="property-table__cell"
                    v-on:click="clickEditValue(item)"
                >
                    <div
                        v-if="!item.editValue"
                        class="property-table__cell__text"
                    >
                        <span v-if="((item.type === 'password') && item.value)">********</span>
                        <span v-else-if="((enableCredentialService && item.type === 'credential') && item.value)">
                            <i class="fa-regular fa-key"></i> {{ item.value.description }}
                        </span>
                        <span v-else>{{item.value}}</span>
                    </div>
                    <input
                        v-if="item.editValue"
                        v-model="item.value"
                        v-on:blur="stopEditValue(item)"
                        v-on:keyup.enter="stopEditValue(item)"
                        :type="item.type"
                        ref="propertyValue"
                        autocomplete="off"
                    />
                </div>
                <div
                    v-if="canDelete"
                    :class="['property-table__cell', item.is_property ? 'property-table__cell--disabled' : '']"
                >
                    <a
                        v-if="!item.is_property"
                        class="btn"
                        href="javascript:"
                        v-on:click="deleteProperty(index)"
                        style="color:#b03630"
                    >
                        <span class="fa fa-trash"></span>
                    </a>
                    <a
                        v-else-if="item.is_property && item.type === 'credential'"
                        class="btn"
                        href="javascript:"
                        v-on:click="resetProperty(index)"
                        style="color:#b03630"
                    >
                        <span class="fa fa-refresh"></span>
                    </a>
                    <a
                        v-else-if="item.is_property"
                        class="btn"
                        href="javascript:"
                        style="color:#CCCCCC"
                    >
                        <span class="fa fa-trash"></span>
                    </a>
                </div>
            </div>
        </div>
        <div class="btn-group btn-property-group">
            <a
                v-if="canAdd && isTableView"
                class="btn btn-property"
                v-on:click="addNewProperty"
                href="javascript:"
            >
                <span class="fa fa-plus"></span> Add value
            </a>
            <slot name="actionButtons"></slot>
        </div>
        </div>
        `,
};
